import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Camera, Eraser, PenLine, X } from 'lucide-react';

// Full-screen pickup capture — designed for handing an iPad to the customer.
// Captures: who picked up (typed name), a photo of the person, and a finger /
// Apple Pencil signature. Timestamp is recorded server-side on submit.
export const PickupSignatureModal = ({ open, onClose, onSubmit, saving = false, defaultName = '' }) => {
  const canvasRef = useRef(null);
  const photoInputRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [name, setName] = useState('');
  const [photo, setPhoto] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(defaultName || '');
    setPhoto(null);
    setHasSignature(false);
    // Clear the canvas after mount
    requestAnimationFrame(() => {
      const c = canvasRef.current;
      if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    });
  }, [open, defaultName]);

  const canvasPoint = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * c.width,
      y: ((e.clientY - r.top) / r.height) * c.height,
    };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastRef.current = canvasPoint(e);
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const p = canvasPoint(e);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasSignature) setHasSignature(true);
  };
  const onPointerUp = () => { drawingRef.current = false; };

  const clearSignature = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
  };

  const onPhotoPicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 900;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const cv = document.createElement('canvas');
        cv.width = Math.round(img.width * scale);
        cv.height = Math.round(img.height * scale);
        cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
        setPhoto(cv.toDataURL('image/jpeg', 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = () => {
    if (!name.trim()) { toast.error('Enter the name of the person picking up'); return; }
    if (!hasSignature) { toast.error('Signature is required'); return; }
    onSubmit({
      picked_up_by: name.trim(),
      signature_base64: canvasRef.current.toDataURL('image/png'),
      photo_base64: photo || null,
    });
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }} data-testid="pickup-modal">
      <div style={{ background: '#fff', width: '100%', maxWidth: 720, maxHeight: '96vh', overflowY: 'auto', borderRadius: 14, boxShadow: '0 25px 80px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
              <PenLine size={18} /> Order Pickup Sign-Off
            </div>
            <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>
              {new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · signature required
            </div>
          </div>
          <button onClick={saving ? undefined : onClose} data-testid="pickup-cancel-btn" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}>
            <X size={20} color="#64748B" />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Name */}
          <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Picked up by (full name)
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name of person picking up"
            data-testid="pickup-name-input"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, marginBottom: 18, padding: '12px 14px', fontSize: 16, border: '1px solid #CBD5E1', borderRadius: 8, outline: 'none' }}
          />

          {/* Photo */}
          <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Photo of person picking up
          </label>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 6, marginBottom: 18 }}>
            {photo ? (
              <img src={photo} alt="Person picking up" data-testid="pickup-photo-preview" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: '1px solid #E2E8F0' }} />
            ) : (
              <div style={{ width: 96, height: 96, borderRadius: 10, border: '1px dashed #CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                <Camera size={26} />
              </div>
            )}
            <div>
              <button
                onClick={() => photoInputRef.current?.click()}
                data-testid="pickup-take-photo-btn"
                style={{ background: '#0F172A', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <Camera size={15} /> {photo ? 'Retake Photo' : 'Take Photo'}
              </button>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>Opens the camera on iPad/phone</div>
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onPhotoPicked} data-testid="pickup-photo-input" />
          </div>

          {/* Signature */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Signature
            </label>
            <button onClick={clearSignature} data-testid="pickup-clear-signature" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#2563EB', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Eraser size={13} /> Clear
            </button>
          </div>
          <canvas
            ref={canvasRef}
            width={1000}
            height={320}
            data-testid="pickup-signature-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{ width: '100%', height: 190, marginTop: 6, background: '#F8FAFC', border: '2px dashed #CBD5E1', borderRadius: 10, touchAction: 'none', cursor: 'crosshair', display: 'block' }}
          />
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 6, textAlign: 'center' }}>
            Sign above with finger or stylus to confirm receipt of this order
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button
              onClick={saving ? undefined : onClose}
              style={{ flex: 1, background: '#fff', border: '1px solid #E2E8F0', color: '#475569', padding: '13px', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              data-testid="pickup-submit-btn"
              style={{ flex: 2, background: hasSignature && name.trim() ? '#10B981' : '#94A3B8', color: '#fff', border: 'none', padding: '13px', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />}
              Confirm Pickup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PickupSignatureModal;
